<template>
  <div class="mcp-app-container" :style="containerStyle">
    <div v-if="loading" class="mcp-app-loading">
      <i class="fa fa-spinner fa-spin fa-2x"></i>
      <p>Loading...</p>
    </div>
    <div v-else-if="error" class="mcp-app-error">
      <i class="fa fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
    </div>
    <iframe
      v-show="!loading && !error"
      ref="iframe"
      :sandbox="sandboxAttrs"
      class="mcp-app-iframe"
      @load="onIframeLoad"
    />
  </div>
</template>

<script>
import { AppBridge, PostMessageTransport } from '@modelcontextprotocol/ext-apps/app-bridge';
import { createAgentsApiHandlers } from '@/services/surfaceApiMcpAdapter';

export default {
  name: 'McpAppRenderer',

  props: {
    // URI of the UI resource to render
    resourceUri: {
      type: String,
      required: true
    },
    // Name of the tool associated with this UI (for direct tool calls)
    toolName: {
      type: String,
      default: null
    },
    // Tool input arguments to send to the guest UI
    toolInput: {
      type: Object,
      default: null
    },
    // Tool result to send to the guest UI
    toolResult: {
      type: Object,
      default: null
    },
    // Surface API base URL
    surfaceUrl: {
      type: String,
      required: true
    },
    // Authentication token
    accessToken: {
      type: String,
      required: true
    },
    // Client ID
    cid: {
      type: String,
      required: true
    },
    // Theme mode: 'dark' or 'light'
    theme: {
      type: String,
      default: 'light'
    },
    // Minimum height for the container
    minHeight: {
      type: Number,
      default: 300
    }
  },

  data() {
    return {
      bridge: null,
      handlers: null, // Store handlers for direct tool calls
      loading: true,
      error: null,
      height: this.minHeight,
      initialized: false,
      sandboxAttrs: 'allow-scripts allow-same-origin allow-forms'
    };
  },

  computed: {
    containerStyle() {
      return {
        minHeight: `${this.height}px`,
        height: this.height ? `${this.height}px` : 'auto'
      };
    }
  },

  watch: {
    // Send tool result when it becomes available
    toolResult: {
      handler(newResult) {
        if (newResult && this.bridge && this.initialized) {
          this.bridge.sendToolResult(this.toPlain(newResult));
        }
      },
      deep: true
    },

    // Update theme when it changes
    theme(newTheme) {
      if (this.bridge && this.initialized) {
        this.bridge.setHostContext({ theme: newTheme });
      }
    }
  },

  async mounted() {
    await this.initializeApp();
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {
    // Strip Vue reactivity proxies and non-cloneable properties so postMessage works
    toPlain(obj) {
      return JSON.parse(JSON.stringify(obj));
    },

    async initializeApp() {
      try {
        this.loading = true;
        this.error = null;

        // Create Surface API handlers
        this.handlers = createAgentsApiHandlers(
          this.surfaceUrl,
          this.accessToken,
          this.cid,
          this.$store.state
        );

        // Fetch the HTML resource
        const resource = await this.handlers.readResource({ uri: this.resourceUri });
        const html = resource.contents[0].text;

        if (!html) {
          throw new Error('Empty resource content received');
        }

        // Create AppBridge with null client (we use Surface API handlers)
        this.bridge = new AppBridge(
          null,
          { name: 'CX-Portal', version: '1.0.0' },
          {
            openLinks: {},
            updateModelContext: { text: {} },
            message: { text: {} },
            logging: {}
          },
          {
            hostContext: {
              theme: this.theme,
              displayMode: 'inline',
              availableDisplayModes: ['inline']
            }
          }
        );

        // Register handlers for MCP operations
        this.bridge.onreadresource = async (params) => {
          return this.handlers.readResource(params);
        };

        // Handle size changes from guest UI
        this.bridge.onsizechange = ({ width, height }) => {
          if (height !== undefined) {
            this.height = Math.max(height, this.minHeight);
            this.$emit('size-changed', { width, height: this.height });
          }
        };

        // Handle messages from guest UI
        this.bridge.onmessage = async (params) => {
          this.$emit('message', params);
          return {};
        };

        // Handle model context updates
        this.bridge.onupdatemodelcontext = async (params) => {
          this.$emit('context-update', params);
          return {};
        };

        // Handle link opening
        this.bridge.onopenlink = async (params) => {
          window.open(params.url, '_blank', 'noopener,noreferrer');
          return {};
        };

        // Handle logging from guest UI
        this.bridge.onloggingmessage = (params) => {
          const level = params.level || 'info';
          const logger = params.logger || 'guest';
          console[level]?.(`[${logger}]`, params.data) || console.log(`[${logger}]`, params.data);
        };

        // Handle tool calls from guest UI (MCP Apps spec)
        this.bridge.oncalltool = async (params) => {
          console.log('Guest UI calling tool:', params.name, params.arguments);
          try {
            const result = await this.handlers.callTool(params);
            return this.toPlain(result);
          } catch (err) {
            console.error('Tool call failed:', err);
            return {
              content: [{ type: 'text', text: `Error: ${err.message}` }],
              isError: true
            };
          }
        };

        // Handle initialization completed
        this.bridge.oninitialized = async () => {
          console.log('MCP App initialized');
          console.log('  - toolInput:', this.toolInput);
          console.log('  - toolResult:', this.toolResult);
          console.log('  - toolName:', this.toolName);
          console.log('  - handlers:', !!this.handlers);
          this.initialized = true;
          this.loading = false;

          // Send tool input if available (default to empty object for tools with no args)
          const inputArgs = this.toolInput || {};
          console.log('Sending tool input:', inputArgs);
          this.bridge.sendToolInput(this.toPlain({ arguments: inputArgs }));

          // Send tool result if already available
          if (this.toolResult) {
            console.log('Sending existing tool result:', this.toolResult);
            this.bridge.sendToolResult(this.toPlain(this.toolResult));
          } else if (this.toolName && this.handlers) {
            // No tool result yet but we have tool name - call the tool directly
            console.log(`Calling tool "${this.toolName}" directly with input:`, inputArgs);
            try {
              const result = await this.handlers.callTool({
                name: this.toolName,
                arguments: inputArgs
              });
              console.log(`Tool "${this.toolName}" raw result:`, JSON.stringify(result, null, 2));

              // Format and send the result to the guest UI
              // Handle double-wrapped CallToolResult (where structuredContent contains the actual result)
              let actualContent = result?.content;
              let actualStructuredContent = result?.structuredContent;

              // Check if structuredContent itself contains content/structuredContent (double-wrapped)
              if (actualStructuredContent?.content && actualStructuredContent?.structuredContent) {
                console.log('Detected double-wrapped CallToolResult, unwrapping...');
                actualContent = actualStructuredContent.content;
                actualStructuredContent = actualStructuredContent.structuredContent;
              }

              const toolResult = {
                content: actualContent || [{ type: 'text', text: JSON.stringify(result) }],
                structuredContent: actualStructuredContent
              };
              console.log('Formatted tool result to send:', JSON.stringify(toolResult, null, 2));
              this.bridge.sendToolResult(this.toPlain(toolResult));
            } catch (err) {
              console.error(`Failed to call tool "${this.toolName}":`, err);
              this.bridge.sendToolResult({
                content: [{ type: 'text', text: `Error: ${err.message}` }],
                isError: true
              });
            }
          } else {
            console.warn('Cannot call tool - missing toolName or handlers');
          }
        };

        // Connect transport BEFORE writing srcdoc so the host is already
        // listening when the guest app sends its initialize message.
        // (The guest's postMessage fires during script execution, which
        //  is dispatched before the iframe load event reaches the parent.)
        const iframe = this.$refs.iframe;
        const transport = new PostMessageTransport(
          iframe.contentWindow,
          iframe.contentWindow
        );
        await this.bridge.connect(transport);
        console.log('AppBridge transport connected, writing srcdoc');

        // Now load the guest content — the host is ready to receive initialize
        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        iframe.src = blobUrl;

        // Clean up after load
        iframe.addEventListener('load', () => {
          URL.revokeObjectURL(blobUrl);
        }, { once: true });

      } catch (err) {
        console.error('Failed to initialize MCP App:', err);
        this.error = err.message || 'Failed to load visualization';
        this.loading = false;
      }
    },

    onIframeLoad() {
      console.log('Iframe load event fired');
    },

    async cleanup() {
      if (this.bridge) {
        try {
          // Attempt graceful teardown
          await this.bridge.teardownResource({});
        } catch (err) {
          // Ignore teardown errors
          console.warn('Teardown warning:', err);
        }

        try {
          await this.bridge.close();
        } catch (err) {
          // Ignore close errors
        }

        this.bridge = null;
      }
    }
  }
};
</script>

<style scoped>
.mcp-app-container {
  width: 100%;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
}

.mcp-app-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.mcp-app-loading,
.mcp-app-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #6c757d;
}

.mcp-app-loading i,
.mcp-app-error i {
  margin-bottom: 10px;
}

.mcp-app-error {
  color: #dc3545;
}

.mcp-app-error i {
  font-size: 2rem;
}
</style>
